import { useState, useCallback, useEffect } from 'react';
import { motion } from 'motion/react';
import { Delete, Eraser } from 'lucide-react';
import { lightImpact } from '@/utils/haptics';

interface PinPadProps {
  onSubmit: (pin: string) => void;
  disabled?: boolean;
  error?: string | null;
}

const PinPad = ({ onSubmit, disabled, error }: PinPadProps) => {
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (error) {
      setShake(true);
      const timer = setTimeout(() => setShake(false), 500);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleDigit = useCallback(
    async (digit: string) => {
      if (disabled || pin.length >= 4) return;
      await lightImpact();
      const newPin = pin + digit;
      setPin(newPin);
      if (newPin.length === 4) {
        onSubmit(newPin);
      }
    },
    [pin, disabled, onSubmit]
  );

  const handleClear = useCallback(async () => {
    await lightImpact();
    setPin('');
  }, []);

  const handleDelete = useCallback(async () => {
    await lightImpact();
    setPin((prev) => prev.slice(0, -1));
  }, []);

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <div className="flex flex-col items-center w-full max-w-xs mx-auto">
      {/* PIN Dots */}
      <div className="mb-8 flex gap-4">
        {[0, 1, 2, 3].map((i) => (
          <motion.div
            key={i}
            initial={false}
            animate={{
              scale: i < pin.length ? 1.1 : 1,
              backgroundColor: i < pin.length ? '#4F46E5' : 'transparent',
            }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className={`w-4 h-4 rounded-full border-2 transition-colors duration-200 ${
              i < pin.length
                ? 'border-primary-600'
                : 'border-slate-300 dark:border-slate-600'
            }`}
          />
        ))}
      </div>

      {/* Error */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 text-red-500 text-sm text-center px-4 font-medium"
        >
          {error}
        </motion.div>
      )}

      {/* Keypad */}
      <motion.div
        animate={shake ? { x: [-8, 8, -8, 8, 0] } : {}}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-3 gap-3 w-full"
      >
        {digits.map((digit) => (
          <motion.button
            key={digit}
            whileTap={{ scale: 0.92 }}
            onClick={() => handleDigit(digit)}
            disabled={disabled}
            className="aspect-square rounded-2xl bg-slate-100 dark:bg-slate-800 text-2xl font-semibold text-slate-800 dark:text-slate-100 
                       active:bg-slate-200 dark:active:bg-slate-700
                       transition-colors duration-100 min-h-[72px] min-w-[72px] flex items-center justify-center
                       disabled:opacity-40 disabled:cursor-not-allowed select-none"
            aria-label={`Digit ${digit}`}
          >
            {digit}
          </motion.button>
        ))}
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={handleClear}
          disabled={disabled}
          className="aspect-square rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300
                     active:bg-slate-200 dark:active:bg-slate-700 transition-colors duration-100 
                     min-h-[72px] min-w-[72px] flex items-center justify-center disabled:opacity-40"
          aria-label="Clear"
        >
          <Eraser className="w-6 h-6" />
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={() => handleDigit('0')}
          disabled={disabled}
          className="aspect-square rounded-2xl bg-slate-100 dark:bg-slate-800 text-2xl font-semibold text-slate-800 dark:text-slate-100
                     active:bg-slate-200 dark:active:bg-slate-700 transition-colors duration-100 
                     min-h-[72px] min-w-[72px] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed select-none"
          aria-label="Digit 0"
        >
          0
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.92 }}
          onClick={handleDelete}
          disabled={disabled || pin.length === 0}
          className="aspect-square rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300
                     active:bg-slate-200 dark:active:bg-slate-700 transition-colors duration-100 
                     min-h-[72px] min-w-[72px] flex items-center justify-center disabled:opacity-40"
          aria-label="Delete"
        >
          <Delete className="w-6 h-6" />
        </motion.button>
      </motion.div>
    </div>
  );
};

export default PinPad;
